import { useEffect, useRef, type ReactNode } from "react";

// Same fade/24px rise/duration/easing as the public-page motion.div. Observe
// visibility without measuring layout or creating per-section motion runtimes.
export default function Reveal({ children, delay = 0, className = "" }: {
  children: ReactNode; delay?: number; className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    let animation: Animation | undefined;
    let frame: number | undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      frame = requestAnimationFrame(() => {
        animation = element.animate(
          [{ opacity: 0, transform: "translateY(24px)" }, { opacity: 1, transform: "none" }],
          { duration: 500, delay: delay * 1000, easing: "cubic-bezier(0,0,0.58,1)", fill: "forwards" },
        );
        animation.onfinish = () => {
          // Release the compositor layer just as motion.div does at rest;
          // retaining a transform animation changes text antialiasing.
          element.style.opacity = "1";
          element.style.transform = "none";
          animation?.cancel();
        };
      });
    }, { rootMargin: "-100px" });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (frame !== undefined) cancelAnimationFrame(frame);
      animation?.cancel();
    };
  }, [delay]);
  return <div ref={ref} className={className} style={{ opacity: 0, transform: "translateY(24px)" }}>{children}</div>;
}
